import { onMounted, onUnmounted, watch, type Ref } from 'vue';

/**
 * Inject JSON-LD structured data into the document head.
 * Automatically cleans up on unmount.
 */
export function useJsonLd(data: Ref<string | null> | (() => string | null), id?: string) {
    const elements: HTMLScriptElement[] = [];

    function inject() {
        cleanup();
        const value = typeof data === 'function' ? data() : data.value;
        if (!value) return;

        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.textContent = value;
        if (id) el.id = `jsonld-${id}`;
        document.head.appendChild(el);
        elements.push(el);
    }

    function cleanup() {
        elements.forEach(el => el.remove());
        elements.length = 0;
    }

    onMounted(inject);
    onUnmounted(cleanup);

    if (typeof data !== 'function' && 'value' in data) {
        watch(data, inject);
    }
}

/**
 * Inject multiple JSON-LD blocks at once.
 */
export function useJsonLdMultiple(blocks: Array<{ data: Ref<string | null> | (() => string | null); id: string }>) {
    blocks.forEach(block => useJsonLd(block.data, block.id));
}
